import { useState } from "react";
import { Input } from "@/app/admin/_common/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/admin/_common/ui/popover";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/app/admin/_common/ui/sheet";
import { Search, ListFilter, FileUp } from "lucide-react";
import WaitlistFilters from "./WaitlistFilters";
import type { WaitlistFilters as WaitlistFiltersType } from "../../waitlistPageModel";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

const toolbarButtonClassName =
  "flex h-[30px] items-center gap-1.5 rounded-md border border-input bg-white px-2 text-[12.5px] text-foreground hover:bg-muted md:px-3";

export default function WaitlistToolbar({
  search,
  onSearchChange,
  filters,
  onApplyFilters,
  onResetFilters,
  onOpenCsvUpload,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  filters?: WaitlistFiltersType;
  onApplyFilters: (next: WaitlistFiltersType) => void;
  onResetFilters: () => void;
  onOpenCsvUpload: () => void;
}) {
  const [filterOpen, setFilterOpen] = useState(false);
  const isMobile = useIsMobile();

  const filterTrigger = (
    <button type="button" className={toolbarButtonClassName} aria-label="Filter">
      <ListFilter className="size-3.5 shrink-0" size={14} />
      <span className="hidden md:inline">Filter</span>
    </button>
  );

  const filterContent = filters && (
    <WaitlistFilters
      filters={filters}
      onApply={(next) => {
        onApplyFilters(next);
        setFilterOpen(false);
      }}
      onReset={onResetFilters}
      onClose={() => setFilterOpen(false)}
    />
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative max-w-[280px] flex-1">
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <Search className="size-3.5 text-muted-foreground" />
        </div>
        <Input
          type="search"
          placeholder="Search waitlist…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-[30px] pl-9 text-[12.5px]"
          aria-label="Search waitlist"
        />
      </div>

      {isMobile ? (
        <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
          <SheetTrigger asChild>{filterTrigger}</SheetTrigger>
          <SheetContent side="bottom" hideClose className="max-h-[85vh] overflow-hidden rounded-t-xl bg-white p-0">
            <SheetTitle className="sr-only">Filters</SheetTitle>
            {filterContent}
          </SheetContent>
        </Sheet>
      ) : (
        <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>{filterTrigger}</PopoverTrigger>
          <PopoverContent align="start" className="w-[350px] p-0">
            {filterContent}
          </PopoverContent>
        </Popover>
      )}

      <button type="button" className={toolbarButtonClassName} onClick={onOpenCsvUpload} aria-label="Import CSV">
        <FileUp className="size-3.5 shrink-0" size={14} />
        <span className="hidden md:inline">Import CSV</span>
      </button>
    </div>
  );
}
